import { routes } from "@bezoned/shared/routes.js";
import { AppEventNames } from "@bezoned/shared/types/app-event-names.js";
import { useQuery } from "@tanstack/react-query";
import { useQueryState } from "nuqs";
import { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { FullScreenLoader } from "../../components/ui/loaders/full-screen-loader.js";
import { teamOfficeGet } from "../../services/api.service.js";
import { useTeamBridgeStore } from "../../store/team-bridge.store.js";
import { trackAuthenticatedOnboardingEvent } from "../onboarding/onboarding-tracker.js";
import { SurveyRenderer } from "../survey/survey-renderer.js";
import { FeatureNotAvailableScreen } from "./feature-not-available-screen.js";
import { NotATeamMemberScreen } from "./not-a-team-member-screen.js";
import { OfficeContext } from "./office-context.js";
import {
  FeatureNotAvailableError,
  TeamOfficeForbiddenError,
} from "./office.errors.js";

export const OfficeProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const [msTeamId] = useQueryState("teamId");
  const hasTrackedOfficeEntry = useRef(false);

  // Sync msTeamId to bridge store for non-React code (API service, Fluid client)
  useEffect(() => {
    useTeamBridgeStore.getState()._setMsTeamId(msTeamId);
  }, [msTeamId]);

  const { data, isLoading, error } = useQuery({
    enabled: Boolean(msTeamId),
    queryFn: () => {
      if (!msTeamId) {
        return null;
      }

      return teamOfficeGet({
        enterOffice: true,
        msTeamId,
      });
    },
    queryKey: ["teamOfficeGet", msTeamId],
    retry: false,
  });

  useEffect(() => {
    if (data && !hasTrackedOfficeEntry.current) {
      hasTrackedOfficeEntry.current = true;
      trackAuthenticatedOnboardingEvent(AppEventNames.OfficeEntered);
    }
  }, [data]);

  if (!msTeamId) {
    return (
      <Navigate
        replace
        to={`${routes.protected.chooseTeam}${location.search}`}
      />
    );
  }

  if (error) {
    if (error instanceof TeamOfficeForbiddenError) {
      return <NotATeamMemberScreen />;
    }

    if (error instanceof FeatureNotAvailableError) {
      return <FeatureNotAvailableScreen reason={error.reason} />;
    }

    throw error;
  }

  if (isLoading || !data) {
    return <FullScreenLoader message="Loading office" />;
  }

  return (
    <OfficeContext.Provider
      value={{
        __ctx_init: true,
        ...data,
      }}
    >
      <SurveyRenderer msTeamId={msTeamId} />
      {children}
    </OfficeContext.Provider>
  );
};
